import { useController, useFormContext } from 'react-hook-form';
import { Card, CardContent, CardTitle } from '@/components/ui/Card.tsx';
import Chip from '@/components/ui/Chip.tsx';
import InputError from '@/components/form/base/InputError.tsx';
import { cn } from '@/lib/utils.ts';
import { ClientInputFormWithCustomFields } from '@/features/public/booking/schemas/clientSchema.ts';
import {
  PublicTransportSelectorDto,
  PublicTransportVariantDto,
} from '@/features/public/booking/types/PublicTransportSelectorDto.ts';
import {
  findTransportPriceForAge,
  transportPriceToMoney,
} from '@/features/public/booking/util/transportPricing.ts';
import { getPricesDisplayString } from '@/features/shared/pricing/util/PricingFormatter.ts';
import { calculateAge } from '@/utils/ageUtils.ts';

interface ParticipantTransportSelectProps {
  index: number;
  transport?: PublicTransportSelectorDto | null;
  tourStartDate: string;
  /** Komunikaty z wyceny dotyczące transportu tego uczestnika. */
  quoteErrors?: string[];
}

const ParticipantTransportSelect = ({
  index,
  transport,
  tourStartDate,
  quoteErrors,
}: ParticipantTransportSelectProps) => {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<ClientInputFormWithCustomFields>();

  const birthDate = watch(`participants.${index}.birthDate`);

  const { field } = useController({
    name: `participants.${index}.transportVariantId`,
    control,
  });

  if (!transport || transport.variants.length === 0) {
    return null;
  }

  const age =
    birthDate instanceof Date
      ? calculateAge(birthDate, new Date(tourStartDate))
      : undefined;

  const handleSelect = (variantId: string) => {
    if (field.value === variantId) {
      // allow deselecting only when transport is optional
      field.onChange(transport.mandatory ? variantId : undefined);
    } else {
      field.onChange(variantId);
    }
  };

  return (
    <div className="space-y-2">
      <CardTitle className="pt-2">
        Transport
        {transport.mandatory && <span className="text-error-600"> *</span>}
      </CardTitle>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {transport.variants.map((variant) => (
          <TransportVariantCard
            key={variant.id}
            variant={variant}
            age={age}
            selected={field.value === variant.id}
            onSelect={() => handleSelect(variant.id)}
          />
        ))}
      </div>
      <div className="flex flex-col">
        <InputError
          error={errors.participants?.[index]?.transportVariantId?.message}
        />
        {quoteErrors?.map((error) => (
          <InputError key={error} error={error} />
        ))}
      </div>
    </div>
  );
};

const TransportVariantCard = ({
  variant,
  age,
  selected,
  onSelect,
}: {
  variant: PublicTransportVariantDto;
  age: number | undefined;
  selected: boolean;
  onSelect: () => void;
}) => {
  const price =
    age !== undefined ? findTransportPriceForAge(variant, age) : undefined;

  const hasEligiblePrice = price !== undefined;
  const selectable = !variant.soldOut && hasEligiblePrice;

  return (
    <Card
      role="button"
      aria-pressed={selected}
      aria-disabled={!selectable}
      onClick={() => selectable && onSelect()}
      className={cn(
        'transition-colors',
        selectable ? 'cursor-pointer hover:border-accent-400' : 'opacity-60',
        selected && 'bg-accent-50 border-accent-600',
      )}
    >
      <CardContent className="flex h-full flex-col justify-between space-y-2 p-3">
        <div className="space-y-1">
          <div className="flex flex-row justify-between gap-2">
            <p className="text-sm font-medium leading-[120%] text-base-text">
              {variant.name}
            </p>
            {price && (
              <p
                className={cn(
                  'text-xs',
                  selected
                    ? 'font-bold text-base-text'
                    : 'font-normal text-neutral-600',
                )}
              >
                {getPricesDisplayString([
                  transportPriceToMoney(price, variant.currency),
                ])}
              </p>
            )}
          </div>
          {variant.description && (
            <p className="text-sm leading-snug text-neutral-600">
              {variant.description}
            </p>
          )}
        </div>
        {variant.soldOut ? (
          <Chip label="Brak miejsc" tone="error" fill="soft" />
        ) : (
          !hasEligiblePrice && (
            <div className="text-sm text-amber-600">
              Brak dostępnego wariantu dla tego wieku
            </div>
          )
        )}
      </CardContent>
    </Card>
  );
};

export default ParticipantTransportSelect;
